import { Plus } from "lucide-react";
import React, { useEffect, useState, useCallback } from "react";
import "./VoucherEntries.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const ITEMS_PER_PAGE_OPTIONS = [20, 50, 100];
const API_URL = "https://payyug.in/api";

const VoucherEntries = () => {
  const navigate = useNavigate();

  const [vouchers, setVouchers] = useState([]);

  // input filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // applied filters
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  /* ================= FETCH ================= */
  const fetchVouchers = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const superAdminId = localStorage.getItem("adminuserId");

      const res = await axios.get(
        `${API_URL}/ledger-entry/get-all-voucher`,
        {
          params: {
            superAdminId,
            page: currentPage,
            perPage: rowsPerPage,
            fromDate: appliedFromDate || undefined,
            toDate: appliedToDate || undefined,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setVouchers(res?.data?.data || []);

      const pagination = res?.data?.pagination || {};
      setTotalPages(pagination.totalPages || 1);
      setTotalRecords(pagination.totalRecords || 0);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Failed to fetch vouchers");
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, appliedFromDate, appliedToDate]);

  /* ================= FILTER ================= */
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!fromDate || !toDate) {
      toast.error("Please select From Date and To Date");
      return;
    }

    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
    setCurrentPage(1);
    setHasSearched(true);
  };

  /* ================= FETCH AFTER SEARCH ================= */
  useEffect(() => {
    if (!hasSearched) return;
    fetchVouchers();
  }, [fetchVouchers, hasSearched]);

  /* ================= PER PAGE ================= */
  const handleItemsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="voucher-container">
      <h1 className="voucher-heading">Voucher</h1>

      {/* ================= TOP ================= */}
      <section className="voucher-top">
        <div className="voucher-top-redirect">
          <h1>Voucher</h1>
          <div className="voucher-redirect-btns">
            <button onClick={() => navigate("/Accountant/Voucher/Payment")}>
              <Plus size={15} /> Payment
            </button>
            <button onClick={() => navigate("/Accountant/Voucher/Receipt")}>
              <Plus size={15} /> Receipt
            </button>
            <button onClick={() => navigate("/Accountant/Voucher/Contra")}>
              <Plus size={15} /> Contra
            </button>
            <button onClick={() => navigate("/Accountant/Voucher/Journal")}>
              <Plus size={15} /> Journal
            </button>
          </div>
        </div>

        {/* ================= FILTER ================= */}
        <form className="voucher-form" onSubmit={handleSubmit}>
          <div className="voucher-form-section">
            <div className="voucher-form-input-fields">
              <label>From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="voucher-form-input-fields">
              <label>To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>

          <div className="voucher-form-submit-btn">
            <button type="submit">
              {loading ? "Loading..." : "Get Records"}
            </button>
          </div>
        </form>
      </section>

      {/* ================= REPORT ================= */}
      <div className="voucher-report-container">
        <h1>Voucher Entries Report</h1>

        <div className="voucher-report-filter-div">
          <div className="voucher-filter">
            <label>Show </label>
            <select value={rowsPerPage} onChange={handleItemsPerPageChange}>
              {ITEMS_PER_PAGE_OPTIONS.map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </select>
            <span> entries</span>
          </div>

          {hasSearched && vouchers.length > 0 && (
            <div className="voucher-total-records">
              Total Records: {totalRecords}
            </div>
          )}
        </div>

        <div className="voucher-report-table-container">
          {error && <p className="error-text">{error}</p>}

          <table className="voucher-report-table">
            <thead>
              <tr>
                <th>SN</th>
                <th>Voucher No.</th>
                <th>Voucher Type</th>
                <th>Narration</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {!hasSearched ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    Please select filters and click Get Records
                  </td>
                </tr>
              ) : vouchers.length > 0 ? (
                vouchers.map((item, index) => (
                  <tr key={item.id}>
                    <td>
                      {(currentPage - 1) * rowsPerPage + index + 1}
                    </td>
                    <td>{item.voucherNo}</td>
                    <td>{item.voucherType}</td>
                    <td>{item.narration}</td>
                    <td>
                      {item.date
                        ? new Date(item.date).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>{item.status || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    {loading ? "Loading..." : "No results found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= PAGINATION ================= */}
      {hasSearched && totalPages > 1 && (
        <div className="voucher-pagination-div">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            ⬅ Prev
          </button>

          <span>
            Page {currentPage} of {totalPages}
          </span>

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next ➡
          </button>
        </div>
      )}
    </div>
  );
};

export default VoucherEntries;
